import React from "react";
import { api } from "../../../api";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  Switch,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Alert,
  AlertIcon,
  Heading,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";
import { useInstitutionConfig } from "./InstitutionConfigContext";

export default function FeatureSettingsTab() {
  const {
    formData,
    institutionConfig,
    config,
    decodedInstitution,
    institution,
    session,
    updating,
    updateConfig,
    refetchInstitutionConfig,
    handleUpdateField,
    toast,
  } = useInstitutionConfig();

  return (
    <VStack spacing={8} align="stretch">
      {/* Feature Toggles */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Feature Settings</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Document Processing</FormLabel>
                <Switch
                  isChecked={formData.enableFisdapShiftProcessing || false}
                  onChange={(e) => handleUpdateField("enableFisdapShiftProcessing", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Shift Scheduling</FormLabel>
                <Switch
                  isChecked={formData.enableShiftScheduling || false}
                  onChange={(e) => handleUpdateField("enableShiftScheduling", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Patient Encounter Tracking</FormLabel>
                <Switch
                  isChecked={formData.enablePatientEncounterTracking || false}
                  onChange={(e) => handleUpdateField("enablePatientEncounterTracking", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Preceptor Evaluations</FormLabel>
                <Switch
                  isChecked={formData.enablePreceptorEvaluations || false}
                  onChange={(e) => handleUpdateField("enablePreceptorEvaluations", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Preceptor Scoring</FormLabel>
                <Switch
                  isChecked={formData.enablePreceptorScoring || false}
                  onChange={(e) => handleUpdateField("enablePreceptorScoring", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Calendar Integration</FormLabel>
                <Switch
                  isChecked={formData.enableCalendarIntegration || false}
                  onChange={(e) => handleUpdateField("enableCalendarIntegration", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Clinical Quick Notes</FormLabel>
                <Switch
                  isChecked={formData.enableClinicalQuickNotes || false}
                  onChange={(e) => handleUpdateField("enableClinicalQuickNotes", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Scenario Generator Settings */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Scenario Generator Settings</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Institution-wide Scenario Generator Access */}
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="institution-scenario-access" mb="0" flex="1">
                <VStack align="start" spacing={1}>
                  <Text color="white">Enable Institutional Scenario Generator Access</Text>
                  <Text fontSize="sm" color="gray.400">
                    When enabled, ALL instructors and staff at this institution can generate clinical scenarios. This does NOT grant access to students - students never get generator access.
                  </Text>
                  <Text fontSize="xs" color="yellow.300" fontWeight="bold">
                    Note: This toggle controls instructor access only. Student enrollment is managed separately via the "Total Student Enrollment Limit" field above.
                  </Text>
                </VStack>
              </FormLabel>
              <Switch
                id="institution-scenario-access"
                isChecked={institutionConfig?.enableScenarioGenerator || false}
                onChange={async (e) => {
                  try {
                    if (!institutionConfig?.id) {
                      const createResult = await api.institutionConfiguration.create({
                        institution: decodedInstitution,
                        enableScenarioGenerator: e.target.checked,
                        timezone: config?.timezone || 'America/New_York',
                        enableFisdapShiftProcessing: config?.enableFisdapShiftProcessing ?? false,
                        enableShiftScheduling: config?.enableShiftScheduling ?? false,
                        enablePatientEncounterTracking: config?.enablePatientEncounterTracking ?? false,
                        enablePreceptorEvaluations: config?.enablePreceptorEvaluations ?? false,
                        enablePreceptorScoring: config?.enablePreceptorScoring ?? false,
                        enableCalendarIntegration: config?.enableCalendarIntegration ?? false,
                        enableClinicalQuickNotes: config?.enableClinicalQuickNotes ?? false,
                        cohortManagementEnabled: config?.cohortManagementEnabled ?? false,
                        subscriptionTier: config?.subscriptionTier || 'individual',
                      });
                      await refetchInstitutionConfig();
                      toast({
                        title: 'Institutional Access Enabled',
                        description: 'Institution configuration created. All non-student members can now generate scenarios.',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                      });
                    } else {
                      await updateConfig({
                        institution: decodedInstitution,
                        configurationUpdates: { enableScenarioGenerator: e.target.checked },
                        reason: 'Updated scenario generator access via toggle'
                      });
                      await refetchInstitutionConfig();
                      toast({
                        title: e.target.checked ? 'Institutional Access Enabled' : 'Institutional Access Disabled',
                        description: e.target.checked
                          ? 'All non-student members can now generate scenarios'
                          : 'Institutional scenario generation disabled',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                  } catch (error) {
                    toast({
                      title: 'Error',
                      description: error.message || 'Failed to update institutional access',
                      status: 'error',
                      duration: 5000,
                      isClosable: true,
                    });
                  }
                }}
                isDisabled={updating}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="institution-debrief-audio-access" mb="0" flex="1">
                <VStack align="start" spacing={1}>
                  <Text color="white">Enable Live Scenario Debrief Audio</Text>
                  <Text fontSize="sm" color="gray.400">
                    Controls live audio-capture transcription/debrief tooling for evaluated scenarios. Voice-sim debrief remains available.
                  </Text>
                </VStack>
              </FormLabel>
              <Switch
                id="institution-debrief-audio-access"
                isChecked={institutionConfig?.enableScenarioDebriefAudio ?? true}
                onChange={(e) => handleUpdateField("enableScenarioDebriefAudio", e.target.checked)}
                isDisabled={updating || !institutionConfig?.id}
                colorScheme="orange"
              />
            </FormControl>

            {!institutionConfig?.id && (
              <Alert status="info" fontSize="sm" mt={2}>
                <AlertIcon />
                <Text>
                  Institution configuration not yet created. Enabling this toggle will create the configuration and grant scenario access to all non-student members.
                </Text>
              </Alert>
            )}

            {!institutionConfig?.id && (
              <Alert status="warning" fontSize="sm" mt={2}>
                <AlertIcon />
                <Text>
                  Live scenario debrief audio toggle becomes editable after institution configuration is created.
                </Text>
              </Alert>
            )}

            <Divider borderColor="gray.600" />

            <Alert status="info" fontSize="sm">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontWeight="bold">Access Hierarchy:</Text>
                <Text>1. Individual subscriptions (independent, no shared scenarios)</Text>
                <Text>2. Cohort memberships (can see institution's shared scenarios)</Text>
                <Text>3. Institution-wide access (non-students only, sees shared scenarios)</Text>
              </VStack>
            </Alert>

            <Divider borderColor="gray.600" />

            {/* Unlimited Scenarios */}
            <FormControl>
              <HStack justify="space-between">
                <VStack align="start" spacing={0} flex="1">
                  <FormLabel color="white" mb={0}>Unlimited Scenarios</FormLabel>
                  <Text fontSize="sm" color="gray.400">Users at this institution have unlimited scenario generation</Text>
                </VStack>
                <Switch
                  isChecked={formData.institutionUnlimitedScenarios || false}
                  onChange={(e) => handleUpdateField("institutionUnlimitedScenarios", e.target.checked)}
                  colorScheme="green"
                />
              </HStack>
            </FormControl>

            {!formData.institutionUnlimitedScenarios && (
              <FormControl>
                <FormLabel color="white">Default Scenario Limit</FormLabel>
                <HStack>
                  <NumberInput
                    value={formData.defaultScenarioLimit || 10}
                    min={0}
                    max={1000}
                    bg="gray.700"
                    borderColor="gray.600"
                    color="white"
                    w="200px"
                  >
                    <NumberInputField
                      onBlur={(e) => {
                        const newValue = parseInt(e.target.value) || 10;
                        handleUpdateField("defaultScenarioLimit", newValue);
                      }}
                    />
                  </NumberInput>
                  <Text fontSize="sm" color="gray.400">scenarios per user (0 = blocked)</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  This is the default limit for users. Individual users and cohorts can override this.
                </Text>
              </FormControl>
            )}

            <Divider borderColor="gray.600" />

            {/* Enhanced Scenarios */}
            <FormControl>
              <HStack justify="space-between">
                <VStack align="start" spacing={0} flex="1">
                  <FormLabel color="white" mb={0}>Enable Enhanced Scenarios</FormLabel>
                  <Text fontSize="sm" color="gray.400">Enhanced scenarios use GPT-5 (slow, 2-5 min, very detailed). Basic scenarios use GPT-4o (fast, 30-60 sec, standard detail).</Text>
                </VStack>
                <Switch
                  isChecked={formData.enableEnhancedScenarios || false}
                  onChange={(e) => handleUpdateField("enableEnhancedScenarios", e.target.checked)}
                  colorScheme="purple"
                />
              </HStack>
              {formData.enableEnhancedScenarios && (
                <Alert status="info" mt={3}>
                  <AlertIcon />
                  <Box>
                    <Text fontSize="sm" fontWeight="bold">Enhanced Scenarios Enabled</Text>
                    <Text fontSize="xs">Users will see a toggle to choose between Basic (fast, GPT-4o, 30-60 sec) and Enhanced (slow, GPT-5, 2-5 min) generation. Enhanced scenarios provide more clinical detail but take significantly longer to generate.</Text>
                  </Box>
                </Alert>
              )}
            </FormControl>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
}
